import { Dinero } from "dinero.js";
import React from "react";
import { defaults } from "tsi-common-react/src/apps/checkout/defaults";
import { FinancingModalTriggerBannerMinicart } from "tsi-common-react/src/apps/financing/FinancingModalTrigger";
import { Link } from "tsi-common-react/src/components/Link/index";
import { SrFootnote } from "tsi-common-react/src/components/SrFootnote/index";
import { useMountEffect } from "tsi-common-react/src/hooks/useMountEffect";
import { IBasket } from "tsi-common-react/src/models/catalogue";
import { isoMonths } from "tsi-common-react/src/models/nominals";
import { useAppSelector } from "tsi-common-react/src/store/reducers";
import { listFinancingPlans } from "tsi-common-react/src/utils/financing";
import { format } from "tsi-common-react/src/utils/format";
import { getDinero, getMonthlyPrice } from "tsi-common-react/src/utils/money";
import { usePageURL } from "tsi-common-react/src/utils/urls";
import { c, t } from "ttag";

interface FinancePlan {
    termLength: string;
    termSuperscript: string;
    termThreshold: string;
}

const calcFinancePlan = (basket: IBasket | null): FinancePlan => {
    const total = basket ? getDinero(basket.total_excl_tax) : getDinero(0);

    const plans = listFinancingPlans();
    const availablePlans = plans.filter((plan) => {
        return plan.threshold.lessThanOrEqual(total);
    });

    if (availablePlans.length) {
        const bestPlan = availablePlans[availablePlans.length - 1];

        return {
            termLength: isoMonths.unwrap(bestPlan.length).toString(),
            termSuperscript: bestPlan.superscript,
            termThreshold: bestPlan.threshold.toUnit().toString(),
        };
    }

    return {
        termLength: "1",
        termSuperscript: "",
        termThreshold: "",
    };
};

const calculatePricePerMonth = (
    basket: IBasket | null,
    termLength: string,
): Dinero => {
    if (!basket) {
        return getDinero(0.0);
    }
    return getMonthlyPrice(
        getDinero(basket.total_excl_tax),
        parseInt(termLength, 10),
    );
};

const shouldShowFinancing = (
    basket: IBasket | null,
    termThreshold: string,
): boolean => {
    if (!basket || !termThreshold) {
        return false;
    }
    return parseFloat(basket.total_excl_tax) >= parseFloat(termThreshold);
};

// TODO: Consolidate Financing Trigger components into one
export const FinancingModalTriggerMinicart = (props: {
    closeModal: () => void;
}): React.ReactNode => {
    const basket = useAppSelector((rootState) => {
        const state = rootState.checkout || defaults;
        return state.data.basket;
    });

    const [termLength, setTermLength] = React.useState("1");
    const [termSuperscript, setTermSuperscript] = React.useState("");
    const [termThreshold, setTermThreshold] = React.useState("");

    const financeLink = usePageURL("finance_link");

    useMountEffect(() => {
        const plan = calcFinancePlan(basket);
        setTermLength(plan.termLength);
        setTermSuperscript(plan.termSuperscript);
        setTermThreshold(plan.termThreshold);
    });

    const pricePerMonth = calculatePricePerMonth(basket, termLength);
    const showFinancing = shouldShowFinancing(basket, termThreshold);

    return (
        <div className="basket-popover-financing">
            {showFinancing && (
                <>
                    <div className="basket-popover-financing__title">
                        {t`or Special Financing`}
                    </div>
                    <div className="basket-popover-financing__terms">
                        {/* TODO(i18n): Use jt to keep sentence together with interpolated components: jt`${format.money(pricePerMonth)}/mo${sup1} for ${termLength} Months${sup2}` */}
                        {format.money(pricePerMonth)}/{t`mo`}
                        <sup>
                            <Link href={financeLink}>
                                2
                                <SrFootnote
                                    text={t`Additional information about Terms and Conditions`}
                                />
                            </Link>
                        </sup>
                        <span className="basket-popover-financing__terms--medium">
                            {t`for`}
                        </span>
                        {c("FinancingModalTriggerMinicart")
                            .t`${termLength} Months`}
                        <sup>
                            <Link href={financeLink}>
                                {termSuperscript}
                                <SrFootnote
                                    text={t`Additional information about Terms and Conditions`}
                                />
                            </Link>
                        </sup>
                    </div>
                </>
            )}
            <FinancingModalTriggerBannerMinicart
                closeModal={props.closeModal}
            />
        </div>
    );
};
